import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Translate, translate } from 'react-jhipster';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, Dialog, DialogContent, DialogTitle, Divider, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'app/config/store';
import { TireDiscardReason } from 'app/shared/model/enumerations/tire-discard-reason.model';
import { TireStatus } from 'app/shared/model/enumerations/tire-status.model';
import { getEntity, deleteEntity, updateEntity, getTireCpkCount } from './tire.reducer';

type DeleteMode = 'options' | 'waste' | 'confirmDelete';

export const TireDeleteDialog = (props: RouteComponentProps<{ id: string }>) => {
  const dispatch = useAppDispatch();
  const hasActed = useRef(false);

  const [mode, setMode] = useState<DeleteMode>('options');
  const [discardReason, setDiscardReason] = useState<TireDiscardReason | null>(null);
  const [tireCpkCount, setTireCpkCount] = useState(0);

  useEffect(() => {
    dispatch(getEntity(props.match.params.id));
  }, []);

  const tireEntity = useAppSelector(state => state.tire.entity);
  const updateSuccess = useAppSelector(state => state.tire.updateSuccess);

  const isWaste = tireEntity.status === TireStatus.WASTE;

  const handleClose = () => {
    props.history.push('/tire' + props.location.search);
  };

  useEffect(() => {
    if (updateSuccess && hasActed.current) {
      handleClose();
    }
  }, [updateSuccess]);

  const confirmWaste = () => {
    if (!discardReason) {
      return toast.error(translate('fleetSenseApp.tire.delete.wasteReasonRequired'));
    }

    hasActed.current = true;
    dispatch(
      updateEntity({
        ...tireEntity,
        status: TireStatus.WASTE,
        discardReason,
      })
    );
  };

  const startDelete = async () => {
    if (tireEntity.id == null) {
      return;
    }

    const count = await dispatch(getTireCpkCount(tireEntity.id)).unwrap();
    setTireCpkCount(count);
    setMode('confirmDelete');
  };

  const permanentConfirmKey =
    tireCpkCount === 0
      ? 'fleetSenseApp.tire.delete.permanentConfirmZero'
      : tireCpkCount === 1
      ? 'fleetSenseApp.tire.delete.permanentConfirmOne'
      : 'fleetSenseApp.tire.delete.permanentConfirmMany';

  const confirmDelete = () => {
    if (tireEntity.id == null) {
      return;
    }
    hasActed.current = true;
    dispatch(deleteEntity(tireEntity.id));
  };

  const noHoverSx = (color: 'success' | 'error') => ({
    bgcolor: `${color}.main`,
    '&:hover': { bgcolor: `${color}.main` },
    boxShadow: 'none',
  });

  const backButtonSx = {
    color: 'text.primary',
    border: '1px solid',
    borderColor: 'text.primary',
    '&:hover': { borderColor: 'text.primary', bgcolor: 'transparent' },
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
          bgcolor: 'primary.main',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        data-cy="tireDeleteDialogHeading"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1.5,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          <Translate contentKey="entity.delete.title">Confirm delete operation</Translate>
        </Typography>
        <IconButton onClick={handleClose} size="small" aria-label="Fechar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      {mode === 'options' && (
        <DialogContent sx={{ py: 2.5 }}>
          <Stack spacing={2}>
            <Typography>
              <Translate contentKey="fleetSenseApp.tire.delete.chooseOperation" interpolate={{ id: tireEntity.marking }}>
                Choose an operation for this tire
              </Translate>
            </Typography>

            {!isWaste && (
              <Button variant="contained" color="success" fullWidth disableElevation disableRipple sx={noHoverSx('success')} onClick={() => setMode('waste')}>
                <Translate contentKey="fleetSenseApp.tire.delete.waste">Send to waste</Translate>
              </Button>
            )}
            <Button
              id="app-start-delete-tire"
              data-cy="entityStartDeleteButton"
              variant="contained"
              color="error"
              fullWidth
              disableElevation
              disableRipple
              sx={noHoverSx('error')}
              startIcon={<DeleteOutlineIcon />}
              onClick={startDelete}
            >
              <Translate contentKey="fleetSenseApp.tire.delete.permanent">Delete permanently</Translate>
            </Button>
            <Button variant="outlined" fullWidth disableRipple sx={backButtonSx} onClick={handleClose}>
              <Translate contentKey="entity.action.cancel">Cancel</Translate>
            </Button>
          </Stack>
        </DialogContent>
      )}

      {mode === 'waste' && (
        <DialogContent sx={{ py: 2.5 }}>
          <Stack spacing={2}>
            <div>
              <Typography component="div" gutterBottom>
                <Translate contentKey="fleetSenseApp.tire.delete.wasteReason">Select the discard reason</Translate>
              </Typography>
              <TextField
                value={discardReason ?? ''}
                id="tire-delete-discardReason"
                data-cy="discardReason"
                name="discardReason"
                onChange={e => setDiscardReason(e.target.value as TireDiscardReason)}
                select
                fullWidth
                size="small"
              >
                {Object.keys(TireDiscardReason).map(item => (
                  <MenuItem key={item} value={item}>
                    {translate(`fleetSenseApp.TireDiscardReason.${item}`)}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            <Button
              id="app-confirm-waste-tire"
              data-cy="entityConfirmWasteButton"
              variant="contained"
              color="success"
              fullWidth
              disableElevation
              disableRipple
              sx={noHoverSx('success')}
              onClick={confirmWaste}
            >
              <Translate contentKey="fleetSenseApp.tire.delete.waste">Send to waste</Translate>
            </Button>
            <Button variant="outlined" fullWidth disableRipple sx={backButtonSx} onClick={() => setMode('options')}>
              <Translate contentKey="entity.action.back">Back</Translate>
            </Button>
          </Stack>
        </DialogContent>
      )}

      {mode === 'confirmDelete' && (
        <DialogContent sx={{ py: 2.5 }}>
          <Stack spacing={2}>
            <Typography>
              <Translate contentKey={permanentConfirmKey} interpolate={{ id: tireEntity.marking, count: tireCpkCount }}>
                This will permanently delete the tire and its records
              </Translate>
            </Typography>

            <Button
              id="app-confirm-delete-tire"
              data-cy="entityConfirmDeleteButton"
              variant="contained"
              color="error"
              fullWidth
              disableElevation
              disableRipple
              sx={noHoverSx('error')}
              startIcon={<DeleteOutlineIcon />}
              onClick={confirmDelete}
            >
              <Translate contentKey="entity.action.delete">Delete</Translate>
            </Button>
            <Button variant="outlined" fullWidth disableRipple sx={backButtonSx} onClick={() => setMode('options')}>
              <Translate contentKey="entity.action.back">Back</Translate>
            </Button>
          </Stack>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default TireDeleteDialog;
